import { useMutation } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { InfiniteStatusEnum, postInfiniteStatus } from '../../../repository/infinite.ts'
import { getBottomPadding } from '../../../utils/sessionStorage.ts'
import { InfiniteHeroVariant } from '../types/infiniteLanding.types.ts'
import { InfiniteLandingExpertsOrbitCarousel } from './InfiniteLandingExpertsOrbitCarousel.tsx'
import { InfiniteLandingGlowLineBase } from './InfiniteLandingGlowLineBase.tsx'
import { EventNames } from '../../../types/misc.types.ts'
import { trackEvent } from '../../../utils/appBridge.ts'

const HERO_BACKGROUND = '/images/infinite/footer_visual.png'
const BRAND_WORDMARK = '/images/infinite/infinite_logo.svg'

type InfiniteLandingHeroPageProps = {
  onJoinWaitlistSuccess?: () => void
  primaryUserName?: string
  firstFoldMinHeight?: string
  variant?: InfiniteHeroVariant
}

export const InfiniteLandingHeroPage = ({
  onJoinWaitlistSuccess,
  primaryUserName,
  firstFoldMinHeight,
  variant = 'default'
}: InfiniteLandingHeroPageProps) => {
  const [hasVideoError, setHasVideoError] = useState(false)
  const [hasVideoEnded, setHasVideoEnded] = useState(false)
  const [isVideoReady, setIsVideoReady] = useState(false)
  const [hasReachedContentRevealTime, setHasReachedContentRevealTime] = useState(false)
  const [hasStickyWaitlistCtaBeenAdded, setHasStickyWaitlistCtaBeenAdded] = useState(false)
  const [isStickyWaitlistCtaVisible, setIsStickyWaitlistCtaVisible] = useState(true)
  const [isHeadWaitlistCtaVisible, setIsHeadWaitlistCtaVisible] = useState(true)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const headWaitlistCtaRef = useRef<HTMLDivElement | null>(null)
  const hasPlaybackStartedRef = useRef(false)
  const contentRevealTimeoutRef = useRef<number | null>(null)
  const stickyWaitlistCtaFadeTimeoutRef = useRef<number | null>(null)
  const waitlistCtaClickSourceRef = useRef<'head' | 'sticky'>('head')
  const isStaticHeroVariant = variant === 'onboarded-open' || variant === 'onboarded-club'
  const shouldShowHeroContent = isStaticHeroVariant || hasReachedContentRevealTime || hasVideoError
  const { mutate: joinWaitlist, isPending: isJoinWaitlistInProgress } = useMutation({
    mutationFn: () => postInfiniteStatus({ status: InfiniteStatusEnum.JOINED_WAITLIST }),
    onSuccess: () => {
      if (waitlistCtaClickSourceRef.current === 'sticky') {
        setHasStickyWaitlistCtaBeenAdded(true)
      }
      onJoinWaitlistSuccess?.()
    }
  })

  const onJoinWaitlistClick = (source: 'head' | 'sticky') => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        eventId: 'JoinWaitlist',
        screenType: 'MF_Infinite_Landing'
      }
    })
    if (isJoinWaitlistInProgress) {
      return
    }

    waitlistCtaClickSourceRef.current = source
    joinWaitlist()
  }

  const startContentRevealTimer = () => {
    if (contentRevealTimeoutRef.current !== null) {
      return
    }

    contentRevealTimeoutRef.current = window.setTimeout(() => {
      setHasReachedContentRevealTime(true)
    }, 2000)
  }

  useEffect(() => {
    if (isStaticHeroVariant) {
      return
    }

    if (hasVideoError || hasVideoEnded) {
      return
    }

    const video = videoRef.current
    if (video !== null) {
      const playAttempt = video.play()
      if (playAttempt !== undefined) {
        playAttempt.catch(() => {
          setHasVideoError(true)
        })
      }
    }

    const autoplayGuardTimeout = window.setTimeout(() => {
      if (hasPlaybackStartedRef.current) {
        return
      }

      setHasVideoError(true)
    }, 1200)

    return () => window.clearTimeout(autoplayGuardTimeout)
  }, [hasVideoEnded, hasVideoError, variant])

  useEffect(() => {
    if (variant !== 'default') {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setIsHeadWaitlistCtaVisible(entries?.[0]?.isIntersecting ?? false)
      },
      { threshold: 0.2 }
    )

    if (headWaitlistCtaRef.current !== null) {
      observer.observe(headWaitlistCtaRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [variant])

  useEffect(() => {
    if (!hasStickyWaitlistCtaBeenAdded) {
      return
    }

    if (stickyWaitlistCtaFadeTimeoutRef.current !== null) {
      window.clearTimeout(stickyWaitlistCtaFadeTimeoutRef.current)
    }

    stickyWaitlistCtaFadeTimeoutRef.current = window.setTimeout(() => {
      setIsStickyWaitlistCtaVisible(false)
    }, 2200)

    return () => {
      if (stickyWaitlistCtaFadeTimeoutRef.current !== null) {
        window.clearTimeout(stickyWaitlistCtaFadeTimeoutRef.current)
      }
    }
  }, [hasStickyWaitlistCtaBeenAdded])

  useEffect(() => {
    return () => {
      if (contentRevealTimeoutRef.current !== null) {
        window.clearTimeout(contentRevealTimeoutRef.current)
      }
      if (stickyWaitlistCtaFadeTimeoutRef.current !== null) {
        window.clearTimeout(stickyWaitlistCtaFadeTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div className='relative flex flex-col overflow-hidden bg-black text-center font-libre-franklin' style={{ minHeight: firstFoldMinHeight }}>
      <style>
        {`
          .infinite-hero-video::-webkit-media-controls,
          .infinite-hero-video::-webkit-media-controls-panel,
          .infinite-hero-video::-webkit-media-controls-start-playback-button {
            display: none !important;
            -webkit-appearance: none;
          }
        `}
      </style>
      {hasVideoError || isStaticHeroVariant ? (
        <img
          src={HERO_BACKGROUND}
          alt=''
          className='pointer-events-none absolute bottom-[-120px] left-1/2 z-0 w-[min(118vw,600px)] max-w-none -translate-x-1/2 translate-y-[22%] select-none opacity-95 sm:w-[600px] sm:translate-y-[18%]'
        />
      ) : (
        <>
          <video
            ref={videoRef}
            className={`infinite-hero-video pointer-events-none absolute z-0 h-full w-full object-cover transition-opacity duration-300 ${isVideoReady ? 'visible opacity-100' : 'invisible opacity-0'}`}
            src='/infinite_landing.mp4'
            poster={'https://cdn.indiawealth.in/public/images/transparent-background-mini.png'}
            preload='auto'
            controls={false}
            autoPlay
            muted
            playsInline
            disablePictureInPicture
            onLoadedData={() => {
              setIsVideoReady(true)
            }}
            onPlay={() => {
              hasPlaybackStartedRef.current = true
              setIsVideoReady(true)
              startContentRevealTimer()
            }}
            onPlaying={() => {
              hasPlaybackStartedRef.current = true
              setIsVideoReady(true)
              startContentRevealTimer()
            }}
            onEnded={() => {
              setHasVideoEnded(true)
            }}
            onError={() => {
              setHasVideoError(true)
            }}
          />
        </>
      )}

      <div
        className={`relative z-10 flex min-h-0 flex-1 flex-col items-center px-7 pt-20 transition-[opacity,transform] duration-500 ease-out ${shouldShowHeroContent ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'} ${shouldShowHeroContent ? '' : 'pointer-events-none'}`}
      >
        <img src={BRAND_WORDMARK} alt='PowerUp Infinite' className='h-auto w-[160px] shrink-0' />
        <AnimatePresence mode='wait'>
          {variant === 'waitlist-success' ? (
            <motion.div
              key='waitlist-success'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className='flex w-full flex-col items-center'
            >
              <h1 className='mt-12 max-w-[320px] font-libre-caslon text-2xl leading-[1.22] tracking-[-0.28px] text-white'>
                Incredible.
                <br />
                You&rsquo;re added to the waitlist.
              </h1>
              <div className='mt-3 h-[38.892px] w-[320px] overflow-hidden'>
                <InfiniteLandingGlowLineBase />
              </div>
              <p className='mt-4 max-w-[320px] text-base leading-[1.34] tracking-[-0.2px] text-white/90'>
                Our team will reach out to you in the next few weeks.
              </p>
            </motion.div>
          ) : variant === 'onboarded-club' ? (
            <motion.div
              key='onboarded-club'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className='flex w-full flex-1 flex-col items-center'
            >
              <h1 className='mt-12 max-w-[320px] font-libre-caslon text-2xl leading-[1.22] tracking-[-0.28px] text-white'>
                {primaryUserName?.split(' ')?.[0] ?? ''},
                <br />
                welcome to Infinite.
              </h1>
              <div className='mt-3 h-[38.892px] w-[320px] overflow-hidden'>
                <InfiniteLandingGlowLineBase />
              </div>
              <p className='mt-4 max-w-[320px] text-base leading-[1.34] tracking-[-0.2px] text-white/90'>
                You now have your own private wealth desk. With a team that shares responsibility for your capital.
              </p>
            </motion.div>
          ) : variant === 'onboarded-open' ? (
            <motion.div
              key='onboarded-open'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className='flex w-full flex-1 flex-col items-center'
            >
              <h1 className='mt-12 max-w-[320px] font-libre-caslon text-2xl leading-[1.22] tracking-[-0.28px] text-white'>
                {primaryUserName?.split(' ')?.[0] ?? ''},
                <br />
                your access to Infinite
                <br />
                is now open.
              </h1>
              <div className='mt-3 h-[38.892px] w-[320px] overflow-hidden'>
                <InfiniteLandingGlowLineBase />
              </div>
              <p className='mt-4 max-w-[320px] text-base leading-[1.34] tracking-[-0.2px] text-white/90'>
                Your private wealth desk is ready.
                <br />
                We've reached out to you to get started.
              </p>
            </motion.div>
          ) : (
            <motion.div
              key='default'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className='flex w-full flex-col items-center'
            >
              <h1 className='mt-12 max-w-[320px] font-libre-caslon text-2xl leading-[1.22] tracking-[-0.28px] text-white'>
                A private wealth desk
                <br />
                for your portfolio.
              </h1>
              <div
                ref={headWaitlistCtaRef}
                className='mt-12 w-full max-w-[320px] shrink-0 cursor-pointer rounded-[10px] bg-[#549BFF] py-[15px] text-center text-[11px] font-semibold uppercase tracking-[3.3px] text-white'
                onClick={() => {
                  onJoinWaitlistClick('head')
                }}
              >
                Join the Waitlist
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {((variant === 'default' && !isHeadWaitlistCtaVisible) || hasStickyWaitlistCtaBeenAdded) ? (
        <AnimatePresence>
          {isStickyWaitlistCtaVisible ? (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 16 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className='fixed inset-x-0 z-40 mx-auto w-full max-w-[420px] px-6'
              style={{ bottom: `${getBottomPadding() + 16}px` }}
            >
              <div
                className='w-full cursor-pointer rounded-[10px] bg-[#549BFF] py-[15px] text-center text-[11px] font-semibold uppercase tracking-[3.3px] text-white'
                onClick={() => {
                  onJoinWaitlistClick('sticky')
                }}
              >
                <AnimatePresence mode='wait' initial={false}>
                  <motion.div
                    key={hasStickyWaitlistCtaBeenAdded ? 'added' : 'join'}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.22, ease: 'easeOut' }}
                  >
                    {hasStickyWaitlistCtaBeenAdded ? (
                      <div className='flex items-center justify-center gap-2'>
                        <svg width='13' height='9' viewBox='0 0 13 9' fill='none' xmlns='http://www.w3.org/2000/svg'>
                          <path d='M1.00007 4.39366L4.04613 7.33073C4.4335 7.70424 5.04698 7.70423 5.43435 7.33073L12 1' stroke='white' strokeWidth='2' strokeLinecap='round' />
                        </svg>
                        <span>Added to waitlist</span>
                      </div>
                    ) : (
                      'Join the Waitlist'
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      ) : null}
    </div>
  )
}

export function InfiniteLandingHeroOnboardedClubCarousel() {
  return (
    <InfiniteLandingExpertsOrbitCarousel
      carouselTrackClassName='relative mt-14 h-[150px] w-full'
      withInitialStepDelay
    />
  )
}
